<template>
  <Head title="Bienvenido a Realgestión - Configura tu Negocio" />
  <div class="min-h-screen bg-slate-50 flex flex-col justify-center py-6 px-4 sm:px-6 lg:px-8">
    <div class="w-full max-w-5xl mx-auto flex flex-col justify-center">
      <!-- Logo Superior -->
      <div class="text-center mb-4">
        <logo class="h-8 w-auto max-w-[180px] mx-auto transition-transform duration-200 hover:scale-105" />
      </div>

      <!-- Stepper / Indicador de Pasos Moderno & Sutil -->
      <div class="flex items-center justify-center gap-2 sm:gap-3 mb-5">
        <!-- Paso 1 -->
        <button 
          type="button"
          class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs transition-all duration-150"
          :class="currentStep === 1 
            ? 'bg-orange-50 text-orange-600 font-semibold border border-orange-200 shadow-xs' 
            : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'"
          @click="currentStep = 1"
        >
          <span 
            class="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold"
            :class="currentStep === 1 ? 'bg-orange-600 text-white' : 'bg-slate-100 text-slate-600'"
          >
            <i v-if="currentStep > 1" class="fas fa-check text-[8px]" />
            <span v-else>1</span>
          </span>
          <span>Tipo de Negocio</span>
        </button>

        <i class="fas fa-chevron-right text-[10px] text-slate-300" />

        <!-- Paso 2 -->
        <div 
          class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs transition-all duration-150"
          :class="currentStep === 2 
            ? 'bg-orange-50 text-orange-600 font-semibold border border-orange-200 shadow-xs' 
            : 'bg-white text-slate-400 border border-slate-200'"
        >
          <span 
            class="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold"
            :class="currentStep === 2 ? 'bg-orange-600 text-white' : 'bg-slate-100 text-slate-400'"
          >
            2
          </span>
          <span>Datos Fiscales</span>
        </div>
      </div>

      <!-- Contenedor Principal Card -->
      <div class="bg-white rounded-3xl shadow-sm border border-slate-200/80 overflow-hidden">
        <!-- ══ PASO 1: Selección de Tipo de Negocio (3 Arriba y 2 Abajo centrados) ══ -->
        <div v-if="currentStep === 1" class="p-6 sm:p-8">
          <!-- Encabezado con Espaciado Definido -->
          <div class="flex flex-col items-center gap-2 text-center max-w-2xl mx-auto mb-6">
            <span class="inline-flex items-center gap-1 px-3 py-0.5 rounded-full bg-orange-100/70 text-orange-600 text-[11px] font-semibold uppercase tracking-wider">
              PASO 1 DE 2
            </span>
            <h1 class="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-snug">
              ¡Hola, {{ user?.first_name || 'Emprendedor' }}! ¿Qué tipo de negocio tienes?
            </h1>
            <p class="text-xs sm:text-sm text-slate-500 max-w-lg leading-relaxed">
              Personalizaremos los menús, opciones y herramientas para que se adapten a tu día a día.
            </p>
          </div>

          <!-- Grilla de Tarjetas: 3 Arriba y 2 Abajo centrados -->
          <div class="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5 mb-6">
            <div 
              v-for="(type, idx) in businessTypes" 
              :key="type.id" 
              class="cursor-pointer rounded-2xl p-5 flex flex-col justify-between text-left transition-all duration-200 relative bg-white col-span-1 md:col-span-2 h-full"
              :class="[
                form.business_type === type.id 
                  ? 'border-2 border-orange-600 bg-orange-50/40 shadow-sm' 
                  : 'border border-slate-200 hover:border-slate-300 hover:shadow-sm',
                idx === 3 ? 'md:col-start-2' : ''
              ]"
              @click="form.business_type = type.id"
            >
              <!-- Filled Checkmark Badge en esquina superior derecha -->
              <div 
                v-if="form.business_type === type.id" 
                class="absolute top-4 right-4 text-orange-600"
              >
                <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
              </div>

              <div>
                <!-- Fila de Encabezado: Icono + Título + Sub-etiqueta -->
                <div class="flex items-center gap-3.5 mb-2.5">
                  <div 
                    class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-200"
                    :class="form.business_type === type.id 
                      ? 'bg-orange-600 text-white' 
                      : 'bg-orange-100/70 text-orange-600'"
                  >
                    <i :class="type.icon" class="text-base" />
                  </div>
                  <div class="pr-5 overflow-hidden">
                    <h3 class="font-semibold text-slate-900 text-base leading-tight truncate">
                      {{ type.label }}
                    </h3>
                    <span class="text-xs text-slate-500 font-normal block truncate mt-0.5">
                      {{ type.subtitle }}
                    </span>
                  </div>
                </div>

                <!-- Descripción Corta (Máximo 2 líneas) -->
                <p class="text-xs md:text-sm text-slate-600 leading-relaxed mb-4 line-clamp-2">
                  {{ type.description }}
                </p>
              </div>

              <!-- Feature Tags / Chips (Máximo 2 tags para evitar ruido visual) -->
              <div class="pt-3 border-t border-slate-100 flex items-center gap-2">
                <span 
                  v-for="(mod, mIdx) in type.modules" 
                  :key="mIdx"
                  class="text-xs font-medium px-2.5 py-1 rounded-md transition-colors"
                  :class="form.business_type === type.id 
                    ? 'bg-orange-100/70 text-orange-900 font-semibold' 
                    : 'bg-slate-100 text-slate-700'"
                >
                  {{ mod }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="form.errors.business_type" class="mb-4 text-center text-xs text-red-600 font-medium">
            {{ form.errors.business_type }}
          </div>

          <!-- Botón de siguiente paso: Fondo Naranja y Letras Blancas destacadas -->
          <div class="flex justify-end pt-4 border-t border-slate-100">
            <button 
              type="button"
              style="background-color: #ea580c !important; color: #ffffff !important;"
              class="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm tracking-wide cursor-pointer"
              @click="goToStep2"
            >
              <span class="text-white font-bold">Siguiente: Información del negocio</span>
              <i class="fas fa-arrow-right text-xs text-white" />
            </button>
          </div>
        </div>

        <!-- ══ PASO 2: Información del Negocio (Mismo Estilo Moderno) ══ -->
        <div v-if="currentStep === 2" class="p-6 sm:p-8">
          <div class="flex flex-col items-center gap-2 text-center max-w-xl mx-auto mb-6">
            <span class="inline-flex items-center gap-1 px-3 py-0.5 rounded-full bg-orange-100/70 text-orange-600 text-[11px] font-semibold uppercase tracking-wider">
              PASO 2 DE 2
            </span>
            <h1 class="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-snug">
              Información de tu negocio (Opcional)
            </h1>
            <p class="text-xs sm:text-sm text-slate-500 max-w-lg leading-relaxed">
              Estos datos se usarán en tus facturas y comprobantes. Puedes editarlos en cualquier momento desde Configuración.
            </p>
          </div>

          <!-- Banner Informativo -->
          <div class="mb-5 p-3.5 rounded-2xl bg-amber-50 border border-amber-200/80 flex items-start gap-3 max-w-3xl mx-auto">
            <div class="w-6 h-6 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
              <i class="fas fa-info-circle text-xs" />
            </div>
            <div class="text-xs sm:text-sm text-amber-900 leading-relaxed">
              <span class="font-semibold">¿No tienes estos datos ahora?</span> Puedes <span class="font-semibold underline">omitir este paso</span> y completarlos más adelante desde tu perfil. Los guardaremos como <em>"No definida"</em> para que puedas comenzar a usar la plataforma de inmediato sin bloqueos.
            </div>
          </div>

          <!-- Formulario de Datos -->
          <form class="max-w-3xl mx-auto" @submit.prevent="submit">
            <div class="bg-slate-50/80 rounded-2xl p-5 sm:p-6 border border-slate-200 mb-6 space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Nombre del Negocio -->
                <div>
                  <label class="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Nombre Comercial o Empresa
                  </label>
                  <input 
                    v-model="form.name" 
                    type="text" 
                    class="w-full text-sm py-2 px-3.5 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors bg-white"
                    placeholder="Ej: Inversiones El Sol, C.A." 
                  />
                  <p class="text-[11px] text-slate-400 mt-1">Por defecto: "Negocio de {{ user?.first_name }}".</p>
                </div>

                <!-- Identificación Fiscal -->
                <div>
                  <label class="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Identificación Fiscal (RIF / NIT / Cédula)
                  </label>
                  <input 
                    v-model="form.identification" 
                    type="text" 
                    class="w-full text-sm py-2 px-3.5 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors bg-white"
                    placeholder="Ej: J-12345678-9 (Opcional)" 
                  />
                  <p class="text-[11px] text-slate-400 mt-1">Necesaria para la factura fiscal.</p>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Dirección Fiscal -->
                <div>
                  <label class="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Dirección Fiscal
                  </label>
                  <textarea 
                    v-model="form.address" 
                    rows="2"
                    class="w-full text-sm py-2 px-3.5 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors bg-white"
                    placeholder="Ej: Av. Principal, Local 4, Ciudad... (Opcional)"
                  />
                </div>

                <!-- Teléfono del Negocio -->
                <div>
                  <label class="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Teléfono de Contacto
                  </label>
                  <input 
                    v-model="form.phone" 
                    type="text" 
                    class="w-full text-sm py-2 px-3.5 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors bg-white"
                    placeholder="Ej: +58 412 1234567 (Opcional)" 
                  />
                  <p class="text-[11px] text-slate-400 mt-1">Teléfono comercial para tus clientes.</p>
                </div>
              </div>
            </div>

            <!-- Botones de Acción -->
            <div class="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-100">
              <button 
                type="button" 
                class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 font-semibold text-xs sm:text-sm transition-all"
                @click="currentStep = 1"
              >
                <i class="fas fa-arrow-left text-xs" />
                <span>Volver a Tipo de Negocio</span>
              </button>

              <div class="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                <!-- Botón Omitir -->
                <button 
                  type="button"
                  :disabled="form.processing"
                  class="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50 font-semibold text-xs sm:text-sm transition-all"
                  @click="skipStep"
                >
                  <i class="fas fa-forward text-xs text-slate-400" />
                  <span>Omitir por ahora</span>
                </button>

                <!-- Botón Guardar: Fondo Naranja y Letras Blancas destacadas -->
                <loading-button 
                  :loading="form.processing"
                  style="background-color: #ea580c !important; color: #ffffff !important;"
                  class="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-7 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm tracking-wide cursor-pointer"
                  type="submit"
                >
                  <span class="text-white font-bold">Guardar e Ir al Dashboard</span>
                  <i class="fas fa-arrow-right text-xs text-white" />
                </loading-button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- Pie Informativo Discreto -->
      <div class="text-center mt-3 text-xs text-slate-400 font-medium">
        Realgestión &bull; Sistema integral de facturación, inventario y control de negocios
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Logo from '@/Shared/Logo'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    Head,
    Logo,
    LoadingButton,
  },
  props: {
    account: Object,
    user: Object,
  },
  data() {
    return {
      currentStep: 1,
      form: this.$inertia.form({
        business_type: this.account?.business_type || 'retail',
        name: this.account?.name || '',
        identification: this.account?.identification || '',
        address: this.account?.address || '',
        phone: this.account?.phone || this.user?.phone || '',
      }),
      businessTypes: [
        { 
          id: 'retail', 
          label: 'Minorista o detal', 
          subtitle: '(Minorista)',
          description: 'Tiendas, minimarkets, bodegones y comercio de venta al detal.', 
          icon: 'fas fa-shopping-bag',
          modules: ['Ventas POS', 'Inventario'],
        },
        { 
          id: 'wholesale', 
          label: 'Mayorista o distribuidora', 
          subtitle: '(Mayorista)',
          description: 'Distribuidoras, ventas al mayor, presupuestos y despachos.', 
          icon: 'fas fa-truck-loading',
          modules: ['Cotizaciones', 'Despachos'],
        },
        { 
          id: 'restaurant', 
          label: 'Restaurantes / Bares', 
          subtitle: '(Gastronomía)',
          description: 'Restaurantes, cafeterías, bares, panaderías y delivery.', 
          icon: 'fas fa-utensils',
          modules: ['POS Restaurante', 'Comandas'],
        },
        { 
          id: 'services', 
          label: 'Venta de servicios', 
          subtitle: '(Servicios)',
          description: 'Consultorías, agencias, talleres y servicios profesionales.', 
          icon: 'fas fa-briefcase',
          modules: ['Presupuestos', 'Cobranza'],
        },
        { 
          id: 'subscriptions', 
          label: 'Suscripciones / Membresías', 
          subtitle: '(Suscripciones)',
          description: 'Gimnasios, clubes, academias y cobros periódicos.', 
          icon: 'fas fa-id-card',
          modules: ['Membresías', 'Pagos Recurrentes'],
        },
      ],
    }
  },
  methods: {
    goToStep2() {
      if (!this.form.business_type) {
        this.form.business_type = 'retail'
      }
      this.currentStep = 2
    },
    skipStep() {
      this.form.identification = ''
      this.form.address = ''
      if (!this.form.name) {
        this.form.name = this.account?.name || ('Negocio de ' + (this.user?.first_name || 'Usuario'))
      }
      this.submit()
    },
    submit() {
      this.form.post('/onboarding', {
        preserveScroll: true,
      })
    },
  },
}
</script>
